import { memo } from "react";
import type { FallbackTier, FloodRegionalSnapshot, FloodWatchSnapshot } from "@kmitl/shared";
import { PanelHeader } from "./PanelHeader";

interface Props {
  data: FloodWatchSnapshot | null;
  loading: boolean;
  ageMinutes?: number;
  fallbackTier?: FallbackTier;
  /** FloodDash regional card — owned by App; canal gauges only here (BKK lives in hero). */
  regional?: FloodRegionalSnapshot | null;
  /** Fly the map to a station. */
  onFlyTo?: (lng: number, lat: number, zoom?: number) => void;
}

const BAND_COLOR: Record<FloodWatchSnapshot["band"], string> = {
  normal: "var(--good)",
  watch: "var(--warn)",
  elevated: "var(--bad)",
  critical: "var(--bad)",
};

function FactorBar({ label, value }: { label: string; value: number }) {
  return (
    <div className="flood-factor">
      <span className="mono caption flood-factor-label">{label}</span>
      <div className="flood-factor-track" aria-hidden>
        <div className="flood-factor-fill" style={{ width: `${Math.min(100, value)}%` }} />
      </div>
      <span className="mono caption flood-factor-val">{value}</span>
    </div>
  );
}

/** Nearest canal gauges — BKK score card is shown in FloodAirHeroStrip. */
function CanalStations({
  regional,
  onFlyTo,
}: {
  regional: FloodRegionalSnapshot | null | undefined;
  onFlyTo?: (lng: number, lat: number, zoom?: number) => void;
}) {
  const stations = regional?.canalStations.slice(0, 3) ?? [];
  if (stations.length === 0) return null;
  return (
    <div className="flood-regional">
      <p className="mono eyebrow" style={{ color: "var(--ink-low)", margin: "8px 0 4px" }}>
        CANAL GAUGES · FLOODDASH
      </p>
      {stations.map((s) => (
        <button
          key={`${s.nameEn}-${s.lat}`}
          type="button"
          className="flood-station-btn"
          onClick={() => onFlyTo?.(s.lng, s.lat, 13)}
          title={`Fly to ${s.nameEn || s.nameTh}`}
        >
          <span className="mono flood-station-kind" style={{ color: "var(--ink-mid)" }}>CN</span>
          <span className="flood-station-name" title={s.nameTh}>
            {s.nameEn || s.nameTh}
          </span>
          <span className="mono caption">
            {s.storagePct != null ? `${Math.round(s.storagePct)}% cap` : "—"} · {s.distanceKm} km
          </span>
        </button>
      ))}
    </div>
  );
}

export const FloodWatchPanel = memo(function FloodWatchPanel({
  data,
  loading,
  ageMinutes,
  fallbackTier,
  regional,
  onFlyTo,
}: Props) {
  return (
    <section className="left-section flood-watch" aria-label="Flood watch">
      <PanelHeader
        title="FLOOD WATCH"
        ageMinutes={ageMinutes}
        fallbackTier={fallbackTier}
        source="hii-thaiwater"
      />

      {loading && !data ? (
        <p className="caption mono" style={{ color: "var(--ink-low)" }}>Connecting to HII…</p>
      ) : !data ? (
        <p className="caption mono" style={{ color: "var(--ink-low)" }}>Unavailable</p>
      ) : (
        <>
          <div className="flood-score-row">
            <div
              className="flood-score"
              style={{ color: BAND_COLOR[data.band] }}
              aria-label={`Watch score ${data.score} of 100, ${data.labelEn}`}
            >
              <span className="flood-score-num mono">{data.score}</span>
              <span className="flood-score-den mono">/100</span>
            </div>
            <div className="flood-band">
              <span className="mono eyebrow" style={{ color: BAND_COLOR[data.band] }}>
                {data.labelEn.toUpperCase()}
              </span>
              <span className="caption" style={{ color: "var(--ink-mid)" }}>{data.labelTh}</span>
            </div>
          </div>

          <p className="caption flood-summary">{data.summaryEn}</p>

          <div className="flood-factors">
            <FactorBar label="WATER" value={data.factors.water} />
            <FactorBar label="RAIN" value={data.factors.rain} />
            <FactorBar label="FC 48H" value={data.factors.forecast} />
          </div>

          <div className="flood-meta mono caption">
            {data.precipFc48hMm != null && (
              <span>48h precip {data.precipFc48hMm.toFixed(1)} mm</span>
            )}
            {data.bangPakongDischargeM3s != null && (
              <span>Bang Pakong {Math.round(data.bangPakongDischargeM3s)} m³/s</span>
            )}
          </div>

          {(data.rainStations.length > 0 || data.waterStations.length > 0) && (
            <ul className="flood-station-list">
              {data.waterStations.slice(0, 3).map((s) => (
                <li key={`wl-${s.id}`}>
                  <button
                    type="button"
                    className="flood-station-btn"
                    onClick={() => onFlyTo?.(s.lng, s.lat, 14)}
                    title={`Fly to ${s.nameEn}`}
                  >
                    <span className="mono flood-station-kind" style={{ color: "var(--ink-mid)" }}>WL</span>
                    <span className="flood-station-name">{s.nameEn}</span>
                    <span className="mono caption flood-station-meta">
                      L{s.situationLevel ?? "—"} · {s.distanceKm} km
                    </span>
                  </button>
                </li>
              ))}
              {data.rainStations.slice(0, 3).map((s) => (
                <li key={`rn-${s.id}`}>
                  <button
                    type="button"
                    className="flood-station-btn"
                    onClick={() => onFlyTo?.(s.lng, s.lat, 14)}
                    title={`Fly to ${s.nameEn}`}
                  >
                    <span className="mono flood-station-kind" style={{ color: "var(--ink-mid)" }}>RN</span>
                    <span className="flood-station-name">{s.nameEn}</span>
                    <span className="mono caption flood-station-meta">
                      {s.rain24h != null ? `${s.rain24h.toFixed(1)} mm` : "—"} · {s.distanceKm} km
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <CanalStations regional={regional} onFlyTo={onFlyTo} />

          <p className="caption mono" style={{ color: "var(--ink-low)", marginTop: 6 }}>
            Watch indicator · FloodDash formula · not a forecast
          </p>
        </>
      )}
    </section>
  );
});
